
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { Badge, Box, Menu, MenuItem, Typography, useMediaQuery } from "@mui/material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import IconButton from "../ui/IconButton";
import { Theme, styled, useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { ECountry, useCountry } from "../../contexts/CountryContext";
import { useIntl } from "../../contexts/IntlContext";
import { fetchCameraStatusShared } from "../../services/cameraStatus";
import {
  YoutubeAppNotification,
  getYoutubeNotificationUnreadCount,
  listYoutubeNotifications,
  markYoutubeNotificationsSeen,
  youtubeNotificationsEventName,
} from "../../services/youtubeOwnerConnection";
import { hasOperationalAccess } from "../../utils/authRole";
import { UserMenu } from "../UserMenu";
import Select from "../ui/Select";


const drawerWidth = 240;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  showMenu?: boolean;
  showTheme?: boolean;
  showUserMenu?: boolean;
}

const AppBarStyle = styled(MuiAppBar, {
  shouldForwardProp: (prop: string) => prop !== "open",
})<AppBarProps>(({ theme, open }: { theme: Theme; open?: boolean }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    [theme.breakpoints.up("md")]: {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
  }),
}));

export function AppBar({ open = false, setOpen, showMenu = false, showTheme = false, showUserMenu = false }: AppBarProps) {
  const location = useLocation();
  const { authInfo } = useContext(AuthContext);
  const canOperate = hasOperationalAccess(authInfo?.role);
  const { country, setCountry } = useCountry();
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const notificationsOpen = Boolean(anchorEl);
  const [cameraRows, setCameraRows] = useState<
    Array<{
      establishmentId: string;
      establishmentName: string | null;
      deviceName: string;
      errorReasons?: string;
    }>
  >([]);
  const [youtubeNotifications, setYoutubeNotifications] = useState<YoutubeAppNotification[]>([]);

  const handleDrawerOpen = () => {
    setOpen?.(true);
  };

  const issues = useMemo(() => {
    const rows = cameraRows ?? [];
    return rows
      .map((row) => {
        const reasons = (row.errorReasons || "")
          .split(",")
          .map((r) => r.trim())
          .filter(Boolean);
        return {
          establishment: row.establishmentName?.trim() || row.establishmentId,
          device: row.deviceName,
          reasons,
        };
      })
      .filter((x) => x.reasons.length > 0);
  }, [cameraRows]);

  const issuesCount = issues.length;
  const ownerYoutubeNotifications = useMemo(
    () => (canOperate ? [] : youtubeNotifications),
    [canOperate, youtubeNotifications]
  );
  const youtubeUnreadCount = useMemo(
    () => getYoutubeNotificationUnreadCount(ownerYoutubeNotifications),
    [ownerYoutubeNotifications]
  );
  const notificationsCount = issuesCount + youtubeUnreadCount;

  const translateReason = (reason: string) => {
    const key = `notifications.reason.${reason}`;
    const translated = intl.formatMessage({ id: key });
    return translated === key ? reason : translated;
  };

  const isCameraLogosRoute = useMemo(
    () => /^\/cameras\/[^/]+\/[^/]+\/logos$/.test(location.pathname),
    [location.pathname],
  );

  useEffect(() => {
    if (!showUserMenu || isCameraLogosRoute) return;
    if (canOperate && !notificationsOpen) return;
    let canceled = false;

    const load = async () => {
      try {
        const response = await fetchCameraStatusShared();
        const rows = Array.isArray(response?.data)
          ? response.data.map((row) => ({
            establishmentId: String(row.establishmentId ?? ""),
            establishmentName:
              typeof row.establishmentName === "string"
                ? row.establishmentName
                : null,
            deviceName: String(row.deviceName ?? ""),
            errorReasons:
              typeof row.errorReasons === "string"
                ? row.errorReasons
                : undefined,
          }))
          : [];
        if (!canceled) setCameraRows(rows);
      } catch {
        if (!canceled) setCameraRows([]);
      }
    };

    load();
    const shouldPoll = !canOperate || notificationsOpen;
    if (!shouldPoll) {
      return () => {
        canceled = true;
      };
    }
    const t = window.setInterval(load, 60_000);
    return () => {
      canceled = true;
      window.clearInterval(t);
    };
  }, [canOperate, isCameraLogosRoute, notificationsOpen, showUserMenu]);

  useEffect(() => {
    const loadYoutubeNotifications = () => {
      setYoutubeNotifications(listYoutubeNotifications());
    };
    loadYoutubeNotifications();
    window.addEventListener(youtubeNotificationsEventName, loadYoutubeNotifications);
    window.addEventListener("storage", loadYoutubeNotifications);
    return () => {
      window.removeEventListener(youtubeNotificationsEventName, loadYoutubeNotifications);
      window.removeEventListener("storage", loadYoutubeNotifications);
    };
  }, []);

  return (
    <AppBarStyle color="default" position="fixed" open={open}>
      <Toolbar>
        {showMenu && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 5,
              ...(open && { display: "none" }),
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            justifyContent: { xs: "flex-start", md: "center" },
            alignItems: "center",
            minWidth: 0,
          }}
        >
          <Box
            component="img"
            src="/assets/logo-new.svg"
            alt="Meu Lance"
            sx={{
              height: { xs: "1.9rem", md: "2.7rem" },
              width: "auto",
              maxWidth: { xs: "50vw", md: "560px" },
              objectFit: "contain",
              filter: theme.palette.mode === "light"
                ? "invert(1) sepia(1) saturate(0) hue-rotate(0deg) brightness(0)"
                : "none",
            }}
          />
        </Box>

        <Box sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0, md: 1 },
          ...(open && { display: { xs: "none", md: "flex" } })
        }}>
          {showUserMenu ? (
            <>
              <IconButton
                color="inherit"
                onClick={(e) => {
                  setAnchorEl(e.currentTarget);
                  if (!canOperate) markYoutubeNotificationsSeen();
                }}
                aria-label={intl.formatMessage({ id: "notifications.title" })}
              >
                <Badge color="error" badgeContent={notificationsCount} invisible={notificationsCount === 0}>
                  <NotificationsNoneOutlinedIcon />
                </Badge>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={notificationsOpen}
                onClose={() => setAnchorEl(null)}
                PaperProps={{ sx: { width: 420, maxWidth: "90vw" } }}
              >
                <MenuItem disabled sx={{ opacity: 1 }}>
                  <Typography variant="subtitle2">
                    {intl.formatMessage({ id: "notifications.title" })}
                  </Typography>
                </MenuItem>
                {issuesCount === 0 && ownerYoutubeNotifications.length === 0 ? (
                  <MenuItem disabled>
                    {intl.formatMessage({ id: "notifications.noIssues" })}
                  </MenuItem>
                ) : (
                  <>
                    {ownerYoutubeNotifications.map((item) => (
                      <MenuItem key={item.id} sx={{ whiteSpace: "normal" }}>
                        {item.message}
                      </MenuItem>
                    ))}
                    {issues.slice(0, 20).map((issue, idx) => (
                      <MenuItem key={`${issue.establishment}:${issue.device}:${idx}`} sx={{ whiteSpace: "normal" }}>
                        {intl.formatMessage(
                          { id: "notifications.issueItem" },
                          {
                            establishment: issue.establishment,
                            device: issue.device,
                            reasons: issue.reasons.map(translateReason).join(", "),
                          }
                        )}
                      </MenuItem>
                    ))}
                  </>
                )}
              </Menu>
            </>
          ) : null}
          <Select
            label={intl.formatMessage({ id: 'common.country' })}
            value={country}
            autoWidth
            fullWidth={false}
            onChange={(e) => setCountry(e.target.value as ECountry)}
            sx={{ display: { xs: "none", md: "flex" } }}
          >
            {Object.values(ECountry).map((c) => (
              <MenuItem key={c} value={c}>
                {intl.formatMessage({ id: 'country.' + c })}
              </MenuItem>
            ))}
          </Select>
          {showUserMenu && <UserMenu showTheme={showTheme} showCountry={isMobile} />}
        </Box>
      </Toolbar>
    </AppBarStyle>
  );
}
